/**
 * Niagaku DateTime helper — single source of truth for the web-admin frontend.
 *
 * Three date categories (matches backend taxonomy):
 *   A. Wall-clock date  — "yyyy-MM-dd" string, no time, no zone (e.g. Invoice.Date).
 *   B. Wall-clock datetime — "yyyy-MM-ddTHH:mm:ss" in MYT (e.g. ServiceRequest.scheduledDate).
 *   C. Instant — ISO 8601 with "Z" suffix (e.g. CreatedOn).
 *
 * Hard rules:
 *  - NEVER call new Date("yyyy-MM-dd") or new Date(s + "T00:00:00") — both create local-zone
 *    Date objects whose .toISOString() drifts by +/-8h for Malaysian users.
 *  - Wall-clock dates stay as strings end-to-end. Only convert to Date for instant fields.
 */

const TZ = 'Asia/Kuala_Lumpur';

const DATE_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const DATETIME_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const DISPLAY_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const DISPLAY_DATETIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function partsOf(date, formatter) {
  const map = {};
  for (const p of formatter.formatToParts(date)) map[p.type] = p.value;
  return map;
}

/** Today's calendar date in Malaysia, as "yyyy-MM-dd". */
export function mytToday() {
  return DATE_PARTS_FORMATTER.format(new Date());
}

/** Current wall-clock datetime in Malaysia, as "yyyy-MM-ddTHH:mm:ss". */
export function mytNow() {
  const p = partsOf(new Date(), DATETIME_PARTS_FORMATTER);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}

/**
 * Format a wall-clock date or instant for display as DD/MM/YYYY (Malaysian convention).
 * Accepts: "yyyy-MM-dd" string (bucket A), ISO instant (bucket C), or Date.
 */
export function formatDate(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (isNaN(dt.getTime())) return '';
  const p = partsOf(dt, DISPLAY_DATE_FORMATTER);
  return `${p.day}/${p.month}/${p.year}`;
}

/** Format an instant as DD/MM/YYYY HH:mm in Malaysia time. */
export function formatDateTime(value) {
  if (value == null || value === '') return '';
  const dt = value instanceof Date ? value : new Date(value);
  if (isNaN(dt.getTime())) return '';
  const p = partsOf(dt, DISPLAY_DATETIME_FORMATTER);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/**
 * Validate and pass through a value coming from <input type="date"> (already "yyyy-MM-dd").
 * Returns "" for empty/invalid input. NEVER constructs a Date — that would corrupt the value.
 */
export function parseDateInput(htmlInputValue) {
  if (htmlInputValue == null || htmlInputValue === '') return '';
  if (typeof htmlInputValue === 'string' && ISO_DATE_RE.test(htmlInputValue)) return htmlInputValue;
  // Defensive fallback: if a Date object slipped in, format it in MYT to recover the date the user intended.
  if (htmlInputValue instanceof Date && !isNaN(htmlInputValue.getTime()))
    return DATE_PARTS_FORMATTER.format(htmlInputValue);
  return '';
}

/** Coerce a "yyyy-MM-dd" string or Date to the API wire format for bucket-A fields. */
export function toApiDate(value) {
  return parseDateInput(value);
}

/** Coerce a Date or ISO string to ISO-8601 with "Z" — wire format for bucket-C instants. */
export function toApiInstant(value) {
  if (value == null || value === '') return null;
  const dt = value instanceof Date ? value : new Date(value);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

/** Parse an API instant ("...Z") into a Date suitable for in-app calculations. */
export function fromApiInstant(iso) {
  if (iso == null || iso === '') return null;
  const dt = new Date(iso);
  return isNaN(dt.getTime()) ? null : dt;
}

/**
 * String-only "yyyy-MM-dd" arithmetic for bucket-A wall-clock dates. Avoids any
 * Date-object construction so calendar math doesn't drift across browser zones.
 */
export function asDateString(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    if (ISO_DATE_RE.test(value)) return value;
    if (value.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '' : DATE_PARTS_FORMATTER.format(parsed);
  }
  if (value instanceof Date && !isNaN(value.getTime())) return DATE_PARTS_FORMATTER.format(value);
  return '';
}

export function addDays(dateString, days) {
  const s = asDateString(dateString);
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().substring(0, 10);
}

export function diffDays(endDateString, startDateString) {
  const a = asDateString(endDateString);
  const b = asDateString(startDateString);
  if (!a || !b) return 0;
  const toUtc = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(a) - toUtc(b)) / 86400000);
}

export default {
  TZ,
  mytToday,
  mytNow,
  formatDate,
  formatDateTime,
  parseDateInput,
  toApiDate,
  toApiInstant,
  fromApiInstant,
  asDateString,
  addDays,
  diffDays,
};
